import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import MovieIcon from '@mui/icons-material/Movie';
import { MediaType } from './profileTabs';
import { useEdgeFade } from '../../hooks/useEdgeFade';

export const MEDIA_ITEMS: { value: MediaType; label: string; icon: React.ReactNode }[] = [
  { value: 'game', label: 'ゲーム', icon: <SportsEsportsIcon sx={{ fontSize: '0.9rem' }} /> },
  { value: 'anime', label: 'アニメ', icon: <LiveTvIcon sx={{ fontSize: '0.9rem' }} /> },
  { value: 'movie', label: '映画', icon: <MovieIcon sx={{ fontSize: '0.9rem' }} /> },
];

interface MediaSegmentedControlProps {
  value: MediaType;
  onChange: (e: React.SyntheticEvent, v: MediaType) => void;
  items?: typeof MEDIA_ITEMS;
  /** 登録件数。0 のメディアは数字を出さない(空の項目まで 0 と主張させない) */
  counts?: Partial<Record<MediaType, number>>;
}

const MediaSegmentedControl: React.FC<MediaSegmentedControlProps> = ({
  value, onChange, items = MEDIA_ITEMS, counts,
}) => {
  const { t } = useTranslation('common');
  const scrollerRef = React.useRef<HTMLDivElement>(null);
  const itemRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const [indicator, setIndicator] = React.useState<{ left: number; width: number } | null>(null);
  const mounted = React.useRef(false);

  React.useLayoutEffect(() => {
    const activeIndex = items.findIndex(i => i.value === value);
    const el = itemRefs.current[activeIndex];
    if (!el) return;
    setIndicator({ left: el.offsetLeft, width: el.offsetWidth });

    // 選択中の項目が視界の外にあると「今どこにいるか」が分からなくなるので寄せる。
    // ただし隠れているときだけ、隠れているぶんの最小量だけ。中央へ寄せる実装にすると
    // 選ぶたびに列全体が動いて、他の項目の位置を覚えられなくなる。
    const scroller = scrollerRef.current;
    if (scroller) {
      const PAD = 12;
      const left = el.offsetLeft;
      const right = left + el.offsetWidth;
      if (left - PAD < scroller.scrollLeft) {
        scroller.scrollLeft = Math.max(0, left - PAD);
      } else if (right + PAD > scroller.scrollLeft + scroller.clientWidth) {
        scroller.scrollLeft = right + PAD - scroller.clientWidth;
      }
    }
    mounted.current = true;
  }, [value, items]);

  // 端のフェード。メインタブと同じ方式を共有している
  const fadeMask = useEdgeFade(() => scrollerRef.current, items);

  return (
    // **メディアは今後増える前提。折り返させないこと。**
    // 折り返すとピルが2行になって選択中の位置が行ごと動き、増えるほど破綻する。
    // 幅が足りないときは横スクロールで到達させる（メインタブと同じ思想）。
    <Box
      ref={scrollerRef}
      sx={{
        minWidth: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        mb: 1.5,
        mt: -1,
        maskImage: fadeMask,
        WebkitMaskImage: fadeMask,
        // スクロールバーは出さない。続きがあることは端のフェードが示す。
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          // スクロール前提なので、親が狭くてもピル自体は実寸を保つ
          width: 'max-content',
          position: 'relative',
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          // 丸ピル。バナー上の操作ボタンや「続きを読む」と形を揃える
          borderRadius: '50px',
          p: '3px',
          border: '1px solid rgba(255, 255, 255, 0.12)',
        }}
      >
        {indicator && (
          <Box
            sx={{
              position: 'absolute',
              top: '3px',
              left: `${indicator.left}px`,
              width: `${indicator.width}px`,
              height: 'calc(100% - 6px)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50px',
              transition: mounted.current
                ? 'left 0.2s cubic-bezier(0.4, 0, 0.2, 1), width 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                : 'none',
              pointerEvents: 'none',
            }}
          />
        )}
        {items.map((item, i) => (
          <Box
            key={item.value}
            ref={(el: HTMLDivElement | null) => { itemRefs.current[i] = el; }}
            onClick={(e) => onChange(e, item.value)}
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              // 詰めさせない・折らせない。狭いときはピルごと横スクロールさせる。
              flexShrink: 0,
              whiteSpace: 'nowrap',
              px: { xs: 1.4, sm: 2 },
              py: { xs: 0.4, sm: 0.4 },
              borderRadius: '50px',
              fontSize: { xs: '0.7rem', sm: '0.75rem' },
              fontWeight: value === item.value ? 500 : 400,
              cursor: 'pointer',
              transition: 'color 0.2s ease',
              color: value === item.value ? 'text.secondary' : 'rgba(255, 255, 255, 0.5)',
              userSelect: 'none',
              '&:hover': {
                color: value === item.value ? 'text.secondary' : 'rgba(255, 255, 255, 0.7)',
              },
            }}
          >
            {/* 選択中のアイコンだけアクセント色。文字は色を変えない
                (暗いアクセント色を選ばれると読めなくなるため) */}
            <Box
              component="span"
              sx={{
                display: 'flex',
                color: value === item.value ? 'primary.main' : 'inherit',
                transition: 'color 0.2s ease',
              }}
            >
              {item.icon}
            </Box>
            {t(`media.${item.value}`, { defaultValue: item.label })}
            {/* 件数。選択中でなくても出すことで「どこに何件あるか」が一覧で分かる。
                0 件は出さない(空のメディアが 0 と自己主張しないように)。 */}
            {!!counts?.[item.value] && (
              <Box
                component="span"
                sx={{
                  ml: 0.25,
                  fontSize: '0.85em',
                  fontWeight: 400,
                  opacity: value === item.value ? 0.75 : 0.65,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {counts[item.value]}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default MediaSegmentedControl;
